// src/pages/championships/KnockoutPairingTool.jsx

import React, { useState, useEffect, useCallback } from 'react';
import championshipService from '../../services/championshipService';
import './KnockoutPairingTool.css';

const phaseDisplayNames = {
  'roundOf16': 'Oitavas de Final',
  'quarterFinals': 'Quartas de Final',
  'semiFinals': 'Semifinais',
  'final': 'Final',
  'thirdPlace': 'Disputa de 3º Lugar',
};

const phasePreviousPhaseMap = {
  'roundOf16': 'initial',
  'quarterFinals': 'roundOf16',
  'semiFinals': 'quarterFinals',
  'final': 'semiFinals',
  'thirdPlace': 'semiFinals',
};

// ✅ A assinatura da função agora aceita a nova prop 'initialTeams'
function KnockoutPairingTool({ championshipId, canManage, currentPhase, initialTeams, onPairingComplete, onCancel }) {
  const [qualifiedTeams, setQualifiedTeams] = useState([]);
  const [pairings, setPairings] = useState([]);
  
  // ✅ Estado inicial atualizado para incluir data e hora
  const getInitialPairState = () => ({
    teamA: '', 
    teamB: '',
    date: new Date().toISOString().split('T')[0], // Data de hoje como padrão
    time: '15:00' // Hora padrão
  });
  
  const [currentPair, setCurrentPair] = useState(getInitialPairState());
  
  const [loading, setLoading] = useState(true); // ← Inicia como true
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchQualifiers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const previousPhaseKey = phasePreviousPhaseMap[currentPhase];
      const response = await championshipService.getKnockoutQualifiers(championshipId, previousPhaseKey);
      const allQualified = response.data.data || [];
      
      setQualifiedTeams(allQualified);
      setPairings([]);
      setSuccess(''); 

    } catch (err) {
        const errorMessage = err.error || err.message || 'Erro ao buscar times classificados.';
        if (typeof errorMessage === 'string' && errorMessage.includes('fase de grupos precisa ser concluída')) {
            setError(`A fase de grupos ainda não foi finalizada. Conclua todas as partidas pendentes para poder gerar o mata-mata.`);
        } else {
            setError(errorMessage);
        }
    } finally {
      setLoading(false);
    }
  }, [championshipId, currentPhase]);

  // ✅ O hook useEffect foi modificado para usar a nova prop
  useEffect(() => {
    if (!canManage) {
      setError('Você não tem permissão para gerenciar o chaveamento manual.');
      setLoading(false);
      return;
    }

    // Se uma lista de times foi passada via props, use-a diretamente.
    if (initialTeams && initialTeams.length > 0) {
      console.log('KnockoutPairingTool: Usando times pré-selecionados recebidos via props.');
      setQualifiedTeams(initialTeams);
      setLoading(false); // Finaliza o loading
    } else {
      // Senão, busca os times no backend (comportamento antigo)
      console.log('KnockoutPairingTool: Buscando times qualificados do backend.');
      fetchQualifiers();
    }
  }, [championshipId, canManage, fetchQualifiers, initialTeams]); // ← initialTeams adicionado às dependências

  const availableTeamsForSelection = qualifiedTeams.filter(
    (team) => !pairings.some(p => p.teamA._id === team._id || p.teamB._id === team._id)
  );
  
  const handleAddPairing = () => {
    setError('');
    setSuccess('');

    const teamA = qualifiedTeams.find(t => t._id === currentPair.teamA);
    const teamB = qualifiedTeams.find(t => t._id === currentPair.teamB);

    // ✅ Validação de data e hora
    if (!teamA || !teamB || teamA._id === teamB._id) {
      setError('Selecione dois times diferentes e válidos.');
      return;
    }
    if (!currentPair.date || !currentPair.time) {
      setError('A data e a hora da partida são obrigatórias.');
      return;
    }

    // ✅ Adiciona o confronto com data e hora ao array de pairings
    setPairings([...pairings, { 
      teamA, 
      teamB,
      date: currentPair.date,
      time: currentPair.time
    }]);
    
    // ✅ Reseta o formulário para o estado inicial
    setCurrentPair(getInitialPairState());
  };

  const handleRemovePairing = (index) => {
    setError('');
    setSuccess('');
    setPairings(pairings.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    if (!canManage) {
      setError('Você não tem permissão para realizar esta ação.');
      return;
    }
    if (pairings.length === 0) {
      setError('Adicione pelo menos um confronto antes de salvar.');
      return;
    }
    
    if ((currentPhase === 'final' || currentPhase === 'thirdPlace') && pairings.length !== 1) {
        setError(`A fase de ${phaseDisplayNames[currentPhase]} deve ter exatamente 1 confronto.`);
        return;
    }
    
    if (currentPhase !== 'final' && currentPhase !== 'thirdPlace' && qualifiedTeams.length > 0 && pairings.length !== qualifiedTeams.length / 2) {
         setError(`Para a fase de ${phaseDisplayNames[currentPhase]}, você deve emparelhar todos os ${qualifiedTeams.length} times qualificados (${qualifiedTeams.length / 2} confrontos).`);
         return;
    }

    setLoading(true);
    try {
      // ✅ Envia os dados completos, incluindo data e hora, para o backend
      const pairingsToSend = pairings.map(p => ({ 
        teamA: p.teamA._id, 
        teamB: p.teamB._id,
        date: p.date,
        time: p.time
      }));
      
      await championshipService.createManualKnockout(championshipId, pairingsToSend, currentPhase);
      setSuccess(`Confrontos de ${phaseDisplayNames[currentPhase]} salvos com sucesso!`);
      setPairings([]);
      if (onPairingComplete) onPairingComplete(); 
    } catch (err) {
      setError(err.error || err.message || 'Falha ao salvar chaveamento manual.');
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return (
      <div className="manual-knockout-container error-message">
        <p>Você não tem permissão para gerenciar o chaveamento manual deste campeonato.</p>
      </div>
    );
  }

  return (
    <div className="manual-knockout-container">
      <h4>Ferramenta de Emparelhamento Manual: {phaseDisplayNames[currentPhase]}</h4>
      
      {loading && <p className="status-message loading">Carregando times...</p>}
      {error && <p className="status-message error">{error}</p>}
      {success && <p className="status-message success">{success}</p>}

      {qualifiedTeams.length === 0 && !loading && !error ? (
        <p className="no-teams-message">Nenhum time disponível para emparelhar. Certifique-se de que a fase anterior foi concluída e que os vencedores foram definidos.</p>
      ) : (
        <>
          <div className="add-pairing-section">
            <select value={currentPair.teamA} onChange={e => setCurrentPair({ ...currentPair, teamA: e.target.value })} disabled={loading}>
              <option value="">-- Selecione o Time 1 --</option>
              {availableTeamsForSelection.map(t => <option key={t._id} value={t._id}>{t.name} ({t.groupName || t.fromPhase})</option>)}
            </select>
            <span>VS</span>
            <select value={currentPair.teamB} onChange={e => setCurrentPair({ ...currentPair, teamB: e.target.value })} disabled={loading}>
              <option value="">-- Selecione o Time 2 --</option>
              {availableTeamsForSelection.map(t => <option key={t._id} value={t._id}>{t.name} ({t.groupName || t.fromPhase})</option>)}
            </select>
            
            {/* ✅ NOVOS INPUTS DE DATA E HORA */}
            <input 
              type="date"
              value={currentPair.date}
              onChange={e => setCurrentPair({ ...currentPair, date: e.target.value })}
              disabled={loading}
              className="pairing-input-datetime"
            />
            <input 
              type="time"
              value={currentPair.time}
              onChange={e => setCurrentPair({ ...currentPair, time: e.target.value })}
              disabled={loading}
              className="pairing-input-datetime"
            />

            <button onClick={handleAddPairing} disabled={loading || !currentPair.teamA || !currentPair.teamB}>Adicionar Confronto</button>
          </div>
          
          <div className="pairings-list">
            <h5>Confrontos Definidos:</h5>
            {pairings.length === 0 ? (
                <p className="no-pairings-message">Nenhum confronto adicionado ainda.</p>
            ) : (
                pairings.map((pair, index) => (
                    <div key={index} className="pairing-item">
                        <div className="pairing-info">
                            <img src={pair.teamA.badge || '/default-team-logo.png'} alt={pair.teamA.name} className="pairing-team-logo" />
                            <span>{pair.teamA.name}</span>
                            <strong style={{ margin: '0 5px' }}>vs</strong> 
                            <span>{pair.teamB.name}</span>
                            <img src={pair.teamB.badge || '/default-team-logo.png'} alt={pair.teamB.name} className="pairing-team-logo" />
                        </div>
                        {/* ✅ EXIBE A DATA E HORA DO CONFRONTO */}
                        <div className="pairing-details">
                            <span>📅 {new Date(pair.date + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                            <span>🕒 {pair.time}</span>
                        </div>
                        <button onClick={() => handleRemovePairing(index)} disabled={loading}>Remover</button>
                    </div>
                ))
            )}
          </div>

          <div className="submit-section">
            <p>Fase a ser gerada: <strong>{phaseDisplayNames[currentPhase]}</strong></p>
            <button onClick={handleSubmit} disabled={loading || pairings.length === 0}>
              {loading ? 'Salvando...' : `Salvar Confrontos de ${phaseDisplayNames[currentPhase]}`}
            </button>
            <button onClick={onCancel} className="cancel-button" disabled={loading}>Cancelar</button>
          </div>
        </>
      )}
    </div>
  );
}

export default KnockoutPairingTool;